import { Cart } from '../../models/Cart'
import { Order } from '../../models/Order'
import { OrderItem } from '../../models/OrderItem'

import { Status } from '@/enums/status'

const mutations = {
  addProductToCart(state: any, orderItem: OrderItem) {
    state.cart.products.push(orderItem)
    
    Cart.updateTotal(state.cart, state.taxRate)
  },
  updateProductInCart(state: any, orderItem: OrderItem) {
    const updatedProduct = state.cart.products.find((product : OrderItem ) => product.productId === orderItem.productId)
    updatedProduct.quantity = orderItem.quantity
    updatedProduct.trimId = orderItem.trimId
    updatedProduct.total = orderItem.total
    if (updatedProduct.installed === true) {
      updatedProduct.installedPrice = updatedProduct.total / updatedProduct.quantity
    } else {
      updatedProduct.price = updatedProduct.total / updatedProduct.quantity
    }

    Cart.updateTotal(state.cart, state.taxRate)
  },
  removeProduct(state: any, orderItem: OrderItem) {
    const productIndex = state.cart.products.indexOf(orderItem)
    if (productIndex != -1) {
      state.cart.products.splice(productIndex, 1)
    }

    Cart.updateTotal(state.cart, state.taxRate)
  },
  setCart(state: any, cart: Cart) {
    state.cart = cart
  },
  clearCart(state: any) {
    state.cart = new Cart({ total: 0, subtotal: 0, tax: 0, products: []})
  },
  setTaxRate(state: any, taxRate: number) {
    state.taxRate = taxRate
  }
}

const getters = {
  products: (state: any) => {
    return state.cart.products
  },
  numProducts: (state: any) => {
    return state.cart.products.length 
  },
  subtotal: (state: any) => {
    return state.cart.subtotal
  },
  taxRate: (state: any) => {
    return state.taxRate
  },
}

const actions = {
  // On log-in, we check to see if the User has an associated Cart (even if it is empty). If there is a localstorage cart pre log-in, it supercedes the cart in the DB. Otherwise, the cart in the DB is loaded.
  async loadCart(context: any, args: any) {
    const orders = await Order.getOrders(context.rootState.auth.currentUser.id)
    let cartExists = false
    orders.forEach((order: Order) => {
      if (order.statusId === Status.CART) {
        cartExists = true 
        if (context.state.cart.subtotal === 0) {  // If the local cart is empty, we load the API cart
          context.dispatch('createCartFromOrder', order)
        } else {
          context.dispatch('createOrder', { statusId: Status.CART, customerId: args.customerId })
        }
      }
    })
    if (cartExists === false) {
      context.dispatch('createOrder', { statusId: Status.CART, customerId: args.customerId })
    }
  },
  // Helper method to convert the Order object from the API into a Cart object for the front-end
  createCartFromOrder(context: any, order: Order) {
    const products : OrderItem[] = []
    const total : number = order.total
    const subtotal : number = order.subtotal
    const tax : number = order.tax
    
    order.orderItems.forEach((item: any) => {
      const p = new OrderItem(item)
      products.push(p)
    })

    const cart = new Cart({ total : total, subtotal: subtotal, tax: tax, products: products })
    context.commit('setCart', cart)
  },
  // Helper method to create an Order object from a Cart object
  async createOrder(context: any, args: any) {
    const orderItems : OrderItem[] = context.state.cart.products
    const customerId = args.customerId
    const sellerId = args.sellerId
    const statusId = args.statusId
    const subtotal = context.state.cart.subtotal
    const total = context.state.cart.total
    const tax = context.state.cart.tax
    const dealerNotes = args.dealerNotes
    const customerNotes = args.customerNotes

    const order : Order = new Order({ statusId: statusId, customerId: customerId, sellerId: sellerId, dealerId: context.rootState.dealers.currentDealer.id, subtotal: subtotal, total: total, tax: tax, orderItems: orderItems, customerNotes: customerNotes, dealerNotes: dealerNotes })
    const orderId = await context.dispatch('pushOrder', order)
    return orderId
  },
  async pushOrder(context: any, order: Order) {
    const response = await Order.pushOrder(order)
    if (order.statusId === Status.ORDER) {
      context.commit('clearCart')
    }

    return response
  },
  async updateCartWithProduct(context: any, args: any) {
    const p = context.state.cart.products.find((orderItem: OrderItem ) => orderItem.productId === args.orderItem.productId)
    if (p === undefined) {
      context.commit('addProductToCart', args.orderItem)
    } else {
      context.commit('updateProductInCart', args.orderItem)
    }

    if (args.auth === true) {
      context.dispatch('createOrder', { statusId: Status.CART, customerId: args.customerId })
    }
  },
  async removeProductFromCart(context: any, args: any) {
    context.commit('removeProduct', args.product)
    if (args.auth === true) {
      context.dispatch('createOrder', { statusId: Status.CART, customerId: args.customerId })
    }
  }
}

const state = () => ({
  cart: new Cart({ total: 0, subtotal: 0, tax: 0, products: [] }),
  taxRate: 0 as number
})

export default {
  namespaced: true,
  mutations,
  getters,
  state,
  actions,
}
